/**
 * Chat Command API
 *
 * Wraps both halves of the command platform's two-surface split (R11):
 *
 *   /api/commands        per-channel USAGE — enable, reword or add a command
 *                        for one channel. Consumed by pages/ChannelCommands.tsx.
 *   /api/admin/commands  global POLICY — platform defaults, the kill switch,
 *                        channel_disableable, and the overridable-key lock.
 *                        Super-admin only. Consumed by pages/AdminCommands.tsx.
 *
 * Both live in one module because they share every type that matters. Splitting
 * them would duplicate the override vocabulary, and two copies of that
 * vocabulary is how the two pages would eventually disagree about what NULL
 * means.
 *
 * Every mutating route returns the FULL refreshed entry, so a caller replaces
 * its row from the response and never needs a follow-up GET — the same
 * load-bearing property adminModulesApi.ts is built on.
 *
 * OMITTED IS NOT NULL, AND THAT IS THE WHOLE POINT OF THE PATCH TYPES.
 * `JSON.stringify` drops `undefined` keys and keeps `null` ones, so a patch
 * object built with `undefined` leaves a field untouched while an explicit
 * `null` clears the override. Never build a patch by spreading a whole form
 * state: that sends every field, and a blank input read as `null` would clear
 * overrides the user never touched. Build the object key by key from what
 * actually changed.
 */

import api from '@/api/client'

/** Where a command came from. Carried on the row, never inferred at read time. */
export type CommandSource = 'base' | 'module' | 'channel'

/** The three fields a channel may be permitted to change. */
export type OverrideKey = 'response_text' | 'permission' | 'cooldown_seconds'

export const OVERRIDE_KEYS: OverrideKey[] = [
  'response_text',
  'permission',
  'cooldown_seconds',
]

export const OVERRIDE_KEY_LABELS: Record<OverrideKey, string> = {
  response_text: 'Response',
  permission: 'Permission',
  cooldown_seconds: 'Cooldown',
}

/**
 * The permission ladder, weakest first — mirrors PERMISSION_LEVELS in
 * core/command_context.py. Inclusive upward: "vip" is satisfied by a VIP, a
 * moderator or the broadcaster. An unrecognised level fails CLOSED at dispatch
 * and logs a warning on every call, which is why the API refuses to store one
 * and this list is not free text.
 */
export const PERMISSION_LEVELS = [
  'everyone',
  'subscriber',
  'vip',
  'moderator',
  'broadcaster',
] as const

export type PermissionLevel = (typeof PERMISSION_LEVELS)[number]

export const PERMISSION_LABELS: Record<string, string> = {
  everyone: 'Everyone',
  subscriber: 'Subscribers',
  vip: 'VIPs',
  moderator: 'Moderators',
  broadcaster: 'Broadcaster only',
}

/** Why a command isn't answering. Null when it is. */
export type UnavailableReason =
  | 'globally_disabled'
  | 'channel_disabled'
  | 'module_disabled'
  | 'unresolvable'

export const UNAVAILABLE_LABELS: Record<UnavailableReason, string> = {
  globally_disabled: 'Disabled platform-wide',
  channel_disabled: 'Off for this channel',
  module_disabled: 'Its module is off for this channel',
  unresolvable: 'No response set',
}

/* ------------------------------------------------------------------ */
/* Global surface — /api/admin/commands                                */
/* ------------------------------------------------------------------ */

/**
 * A command as the platform admin sees it.
 *
 * `handler_backed` and `has_row` are independent and their combinations mean
 * different things: a handler with no row is a code-defined command running on
 * permissive defaults (registered, unlocked, channel-disableable) that has
 * never been governed; a row with no handler is an admin-authored global, where
 * the row IS the command.
 */
export interface CommandEntry {
  name: string
  source: CommandSource
  owner_module: string | null
  display_name: string | null
  description: string | null
  /** The stored answer. NULL means handler-backed — never "empty answer". */
  response_text: string | null
  permission: string
  cooldown_seconds: number
  /** Global kill switch. False removes the command from every channel. */
  enabled: boolean
  channel_disableable: boolean
  overridable_keys: OverrideKey[]
  /** A handler is registered under this name right now. */
  handler_backed: boolean
  /** A chat_commands row exists (as opposed to permissive absent-row defaults). */
  has_row: boolean
  channel_override_count: number
}

export interface CommandCreate {
  name: string
  response_text: string
  display_name?: string | null
  description?: string | null
  permission?: string
  cooldown_seconds?: number
  enabled?: boolean
  channel_disableable?: boolean
  overridable_keys?: OverrideKey[]
}

/** Only the keys present are applied; an explicit null writes NULL. */
export interface CommandPatch {
  display_name?: string | null
  description?: string | null
  response_text?: string | null
  permission?: string
  cooldown_seconds?: number
  enabled?: boolean
  channel_disableable?: boolean
  overridable_keys?: OverrideKey[]
}

export interface CommandDeleted {
  name: string
  deleted: boolean
  /** Per-channel override rows removed with it. A delete cascades. */
  channel_rows_removed: number
}

export const adminCommandsApi = {
  /**
   * The whole catalog: every stored definition, plus every name the in-memory
   * registry holds that has no row yet (reported with permissive defaults).
   */
  list(): Promise<CommandEntry[]> {
    return api.get<CommandEntry[]>('/api/admin/commands')
  },

  /** Create an admin-authored global. 409 on a name already in use. */
  create(data: CommandCreate): Promise<CommandEntry> {
    return api.post<CommandEntry>('/api/admin/commands', data)
  },

  /**
   * Update defaults and policy. Creates the definition row on first write for a
   * code-defined command that has never been governed.
   *
   * 400 on setting response_text for a handler-backed command — its output is
   * computed in code, so there is no text to edit.
   */
  patch(name: string, data: CommandPatch): Promise<CommandEntry> {
    return api.patch<CommandEntry>(
      `/api/admin/commands/${encodeURIComponent(name)}`,
      data,
    )
  },

  /**
   * Delete an admin-authored global. 400 when a handler exists: code-defined
   * commands are disabled, never deleted. CASCADES to per-channel override
   * rows, and reports how many it removed.
   */
  remove(name: string): Promise<CommandDeleted> {
    return api.delete<CommandDeleted>(
      `/api/admin/commands/${encodeURIComponent(name)}`,
    )
  },
}

/* ------------------------------------------------------------------ */
/* Per-channel surface — /api/commands                                 */
/* ------------------------------------------------------------------ */

/**
 * The raw per-channel row, NULL preserved.
 *
 * NULL is "no override". An empty string is a real override meaning "answer
 * nothing". A cooldown of 0 is a real override meaning "no cooldown". Three
 * states, and any control that renders them all as an empty box will collapse
 * them on save.
 */
export interface CommandOverride {
  response_text: string | null
  permission: string | null
  cooldown_seconds: number | null
}

/**
 * A command as one channel sees it.
 *
 * `effective_*` is what chat gets right now — the definition with this
 * channel's ADMITTED overrides applied. `override` is what the channel stored,
 * which is not the same: a locked or handler-backed override is kept and does
 * not win, and `inert_keys` names exactly those.
 *
 * Entries appear even when they do not answer. A name listed here is a name in
 * use, whatever `available` says.
 */
export interface ChannelCommandEntry {
  name: string
  source: CommandSource
  owner_module: string | null
  display_name: string | null
  description: string | null
  /** Output computed by code. There is no response text to edit. */
  handler_backed: boolean
  /** This channel's own command — the only kind it may delete. */
  channel_owned: boolean
  effective_response_text: string | null
  effective_permission: string
  effective_cooldown_seconds: number
  enabled: boolean
  channel_disableable: boolean
  /** Stored as off, but policy forbids it, so it still answers. */
  disable_refused: boolean
  override: CommandOverride
  overridden_keys: OverrideKey[]
  locked_keys: OverrideKey[]
  /** Overridden but not winning — locked, or text on a handler-backed command. */
  inert_keys: OverrideKey[]
  available: boolean
  unavailable_reason: UnavailableReason | null
}

export interface ChannelCommandCreate {
  name: string
  response_text: string
  permission?: string
  cooldown_seconds?: number
}

/** Only the keys present are applied; an explicit null clears that override. */
export interface ChannelCommandPatch {
  enabled?: boolean
  response_text?: string | null
  permission?: string | null
  cooldown_seconds?: number | null
}

export interface ChannelCommandDeleted {
  name: string
  deleted: boolean
}

export const channelCommandsApi = {
  /**
   * This channel's catalog. Also the "names already in use" surface: a name
   * that appears here at all, available or not, is one `create` will refuse.
   */
  list(channelId: number): Promise<ChannelCommandEntry[]> {
    return api.get<ChannelCommandEntry[]>(`/api/commands/${channelId}`)
  },

  /** Add a command of this channel's own. 409 on a name in use. */
  create(
    channelId: number,
    data: ChannelCommandCreate,
  ): Promise<ChannelCommandEntry> {
    return api.post<ChannelCommandEntry>(`/api/commands/${channelId}`, data)
  },

  /**
   * Enable/disable, or set override deltas.
   *
   * 403 on a locked field or a command that isn't channel-disableable; 400 on
   * response text for a handler-backed command. A locked field still accepts
   * null, so an override stranded by a later lock can always be removed.
   */
  patch(
    channelId: number,
    name: string,
    data: ChannelCommandPatch,
  ): Promise<ChannelCommandEntry> {
    return api.patch<ChannelCommandEntry>(
      `/api/commands/${channelId}/${encodeURIComponent(name)}`,
      data,
    )
  },

  /** Delete one of this channel's own commands. 403 for anything else. */
  remove(channelId: number, name: string): Promise<ChannelCommandDeleted> {
    return api.delete<ChannelCommandDeleted>(
      `/api/commands/${channelId}/${encodeURIComponent(name)}`,
    )
  },
}

export default channelCommandsApi
